// src/components/SigningProgressModal.jsx

import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/firebase';
import './SigningProgressModal.css';

export default function SigningProgressModal({
  document,
  project,
  teams,
  isOpen,
  onClose,
  onDownload,
  onRefresh
}) {
  const [submitters, setSubmitters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [userProfiles, setUserProfiles] = useState({});

  const isTeamSignOff = document?.mode === 'team_sign_off';

  // Fetch submitters (DocuSeal) or build from acknowledgements (Team sign-off) when modal opens
  useEffect(() => {
    if (!isOpen || !document) return;
    if (isTeamSignOff) {
      loadTeamSignOffData();
    } else if (document.docusealSubmissionId) {
      // DocuSeal sunset: use Firestore data only - no API calls
      const cached = document.docusealSubmitters || [];
      setSubmitters(cached);
      setLastUpdated(new Date());
      if (cached.length > 0) {
        fetchUserProfiles(cached);
      }
      setLoading(false);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, document, isTeamSignOff]);

  const fetchSubmitters = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Call Firebase function to get submitters
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../firebase/firebase');
      
      const getSubmitters = httpsCallable(functions, 'getDocuSealSubmitters');
      const result = await getSubmitters({
        submissionId: document.docusealSubmissionId
      });
      
      const submittersData = result.data.submitters || [];
      setSubmitters(submittersData);
      setLastUpdated(new Date());
      
      // Fetch user profile pictures for submitters
      await fetchUserProfiles(submittersData);
    } catch (error) {
      console.error('Error fetching submitters:', error);
      setError('Failed to load signing progress');
    } finally {
      setLoading(false);
    }
  };

  const loadTeamSignOffData = async () => {
    try {
      setLoading(true);
      setError(null);
      const acks = document.acknowledgements || {};
      const memberIds = new Set();
      const teamsList = teams || [];
      const relevantTeams = document.teamAssignment === 'all'
        ? teamsList
        : teamsList.filter(t => (document.selectedTeams || []).includes(t.id));
      for (const team of relevantTeams) {
        for (const m of team.members || []) {
          if (m?.id) memberIds.add(m.id);
        }
      }
      if (project) {
        if (project.createdBy) memberIds.add(project.createdBy);
        const collab = project.collaborators || {};
        for (const [userId, data] of Object.entries(collab)) {
          if (data?.status === 'accepted' && data?.role === 'pm_collaborator') memberIds.add(userId);
        }
      }
      const userIds = Array.from(memberIds);
      const nameMap = {};
      const emailMap = {};
      for (const team of relevantTeams) {
        for (const m of team.members || []) {
          if (m?.id) {
            if (m?.name) nameMap[m.id] = m.name;
            if (m?.email) emailMap[m.id] = m.email;
          }
        }
      }
      for (const uid of userIds) {
        if (nameMap[uid]) continue;
        try {
          const u = await getDoc(doc(db, 'users', uid));
          if (u.exists()) {
            const d = u.data();
            nameMap[uid] = d.firstName && d.lastName
              ? `${d.firstName} ${d.lastName}`.trim()
              : d.email || d.name || 'Unknown';
            if (d.email && !emailMap[uid]) emailMap[uid] = d.email;
          } else {
            nameMap[uid] = 'Unknown';
          }
        } catch {
          nameMap[uid] = 'Unknown';
        }
      }
      const signed = userIds.filter(id => acks[id]).map(id => ({
        id,
        name: acks[id].name || nameMap[id] || 'Unknown',
        email: emailMap[id] || '',
        status: 'completed',
        completed_at: acks[id].signedAt
      }));
      const pending = userIds.filter(id => !acks[id]).map(id => ({
        id,
        name: nameMap[id] || 'Unknown',
        email: emailMap[id] || '',
        status: 'pending'
      }));
      setSubmitters([...signed, ...pending]);
      setLastUpdated(new Date());
      const profileIds = [...signed.map(s => s.id), ...pending.map(p => p.id)];
      await fetchUserProfilesById(profileIds);
    } catch (err) {
      console.error('Team sign-off load error:', err);
      setError('Failed to load signing progress');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfilesById = async (userIds) => {
    const profiles = {};
    for (const uid of userIds || []) {
      try {
        const g = await getDoc(doc(db, `global_backup/users/profiles/${uid}`));
        profiles[uid] = g.exists() ? (g.data()?.profilePictureUrl || '/crewchainDefault.png') : '/crewchainDefault.png';
      } catch {
        profiles[uid] = '/crewchainDefault.png';
      }
    }
    setUserProfiles(profiles);
  };

  const fetchUserProfiles = async (submittersData) => {
    try {
      const profiles = {};
      
      for (const submitter of submittersData) {
        if (submitter.email) {
          try {
            // Find user by email to get their userId
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('email', '==', submitter.email));
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
              const userDoc = querySnapshot.docs[0];
              const userId = userDoc.id;
              
              // Fetch profile picture from global_backup (single source of truth)
              try {
                const globalProfileDoc = await getDoc(doc(db, `global_backup/users/profiles/${userId}`));
                if (globalProfileDoc.exists()) {
                  const globalData = globalProfileDoc.data();
                  profiles[submitter.email] = globalData?.profilePictureUrl || '/crewchainDefault.png';
                } else {
                  profiles[submitter.email] = '/crewchainDefault.png';
                }
              } catch (error) {
                console.log(`Could not fetch profile from global_backup for ${userId}:`, error);
                profiles[submitter.email] = '/crewchainDefault.png';
              }
            } else {
              // User not found in Firestore, use default
              profiles[submitter.email] = '/crewchainDefault.png';
            }
          } catch (error) {
            console.error(`Error fetching profile for ${submitter.email}:`, error);
            profiles[submitter.email] = '/crewchainDefault.png';
          }
        }
      }
      
      setUserProfiles(profiles);
    } catch (error) {
      console.error('Error fetching user profiles:', error);
    }
  };

  // const getStatusIcon = (status) => {
  //   switch (status) {
  //     case 'completed':
  //       return '✓';
  //     case 'pending':
  //       return '⏱';
  //     case 'declined':
  //       return '✗';
  //     default:
  //       return '📧';
  //   }
  // };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#10b981';
      case 'pending':
        return '#f59e0b';
      case 'declined':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Signed';
      case 'pending':
        return 'Pending';
      case 'declined':
        return 'Declined';
      default:
        return 'Not Started';
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'Not signed';
    const d = dateValue?.toDate ? dateValue.toDate() : new Date(dateValue);
    return isNaN(d.getTime()) ? 'Not signed' : d.toLocaleString();
  };

  const calculateProgress = () => {
    if (!submitters.length) return 0;
    const completed = submitters.filter(s => s.status === 'completed').length;
    return Math.round((completed / submitters.length) * 100);
  };

  const getCompletedCount = () => {
    return submitters.filter(s => s.status === 'completed').length;
  };

  const getTotalCount = () => submitters.length;
  const getProgressPct = () => {
    const tot = getTotalCount();
    if (!tot) return 0;
    return Math.min(100, Math.round((getCompletedCount() / tot) * 100));
  };
  const getProfileUrl = (s) => (s.id ? userProfiles[s.id] : userProfiles[s.email]) || '/crewchainDefault.png';

  // Sort submitters: completed first, then pending, then others (alphabetically within each group)
  const sortedSubmitters = () => {
    return [...submitters].sort((a, b) => {
      const statusA = (a.status || '').toLowerCase();
      const statusB = (b.status || '').toLowerCase();
      
      const isCompletedA = statusA === 'completed' || statusA === 'signed';
      const isCompletedB = statusB === 'completed' || statusB === 'signed';
      
      // Completed signers come first
      if (isCompletedA && !isCompletedB) return -1;
      if (!isCompletedA && isCompletedB) return 1;
      
      // If both completed or both not completed, sort alphabetically by name
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  if (!isOpen) return null;

  return (
    <div className="signing-progress-modal">
      <div className="signing-progress-content">
        <div className="signing-progress-header">
          <div className="header-content">
            <h2>Signing progress</h2>
            <p>{document?.name}</p>
            {lastUpdated && (
              <small>Last updated: {lastUpdated.toLocaleTimeString()}</small>
            )}
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="signing-progress-body">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading signing progress...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              {!document?.docusealSubmissionId && (
                <button onClick={fetchSubmitters} className="retry-btn">
                  Try Again
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Overall Progress */}
              <div className="overall-progress">
                <div className="progress-header">
                  <h3>Overall progress</h3>
                  <span className="progress-text">
                    {getCompletedCount()} of {getTotalCount()} signed
                  </span>
                </div>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${isTeamSignOff ? getProgressPct() : calculateProgress()}%` }}
                  />
                </div>
                <div className="progress-percentage">
                  {isTeamSignOff ? getProgressPct() : calculateProgress()}% Complete
                </div>
              </div>

              {/* Recipients List */}
              <div className="recipients-section">
                <h3>Recipients ({getTotalCount()})</h3>
                <div className="recipients-list">
                  {sortedSubmitters().map((submitter) => (
                    <div key={submitter.id || submitter.email || submitter.name} className="recipient-card">
                      <div className="recipient-header">
                        <div className="recipient-info">
                          <img
                            src={getProfileUrl(submitter)}
                            alt={submitter.name}
                            className="recipient-avatar"
                          />
                          <div className="recipient-details">
                            <strong>{submitter.name}</strong>
                            <span className="recipient-email">{submitter.email}</span>
                            {submitter.phone && submitter.phone.trim() !== '' && (
                              <span className="recipient-phone">{submitter.phone}</span>
                            )}
                          </div>
                        </div>
                        <div className="recipient-status">
                          <span 
                            className="status-badge"
                            style={{ backgroundColor: getStatusColor(submitter.status) }}
                          >
                            {getStatusText(submitter.status)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="recipient-timeline">
                        {!isTeamSignOff && submitter.sent_at && (
                          <div className="timeline-item">
                            <span className="timeline-label">Sent:</span>
                            <span className="timeline-value">
                              {formatDate(submitter.sent_at)}
                            </span>
                          </div>
                        )}
                        {!isTeamSignOff && submitter.opened_at && (
                          <div className="timeline-item">
                            <span className="timeline-label">Opened:</span>
                            <span className="timeline-value">
                              {formatDate(submitter.opened_at)}
                            </span>
                          </div>
                        )}
                        {submitter.completed_at && (
                          <div className="timeline-item completed">
                            <span className="timeline-label">Signed:</span>
                            <span className="timeline-value">
                              {formatDate(submitter.completed_at)}
                            </span>
                          </div>
                        )}
                        {submitter.declined_at && (
                          <div className="timeline-item declined">
                            <span className="timeline-label">Declined:</span>
                            <span className="timeline-value">
                              {formatDate(submitter.declined_at)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="signing-progress-actions">
          {onDownload && (() => {
            const completedCount = getCompletedCount();
            const totalCount = getTotalCount();
            const hasAnySignatures = isTeamSignOff
              ? (completedCount > 0 || document?.signedFileURL)
              : completedCount > 0;
            const isFullySigned = completedCount === totalCount && totalCount > 0;

            return (
              <button
                onClick={hasAnySignatures ? onDownload : undefined}
                disabled={!hasAnySignatures}
                className="download-btn"
                title={!hasAnySignatures ? 'Signed document download will become available when at least one recipient has signed' : undefined}
                style={{
                  background: hasAnySignatures ? '#059669' : '#cbd5e1',
                  color: hasAnySignatures ? '#fff' : '#94a3b8',
                  border: 'none',
                  padding: '0.5rem 1rem',
                  borderRadius: '0.25rem',
                  cursor: hasAnySignatures ? 'pointer' : 'not-allowed',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  opacity: hasAnySignatures ? 1 : 0.6
                }}
              >
                {isFullySigned
                  ? 'Download completed PDF'
                  : hasAnySignatures
                    ? `Download PDF (${completedCount} of ${totalCount} signed)`
                    : 'Download PDF'}
              </button>
            );
          })()}
          <button
            onClick={isTeamSignOff && onRefresh ? onRefresh : (document?.docusealSubmissionId ? undefined : fetchSubmitters)}
            disabled={!!document?.docusealSubmissionId}
            className="refresh-btn"
            title={document?.docusealSubmissionId ? 'Legacy document - refresh unavailable' : undefined}
          >
            Refresh
          </button>
          <button onClick={onClose} className="close-btn">
            Close
          </button>
        </div>
      </div>
    </div>
  );
} 